/**
 * =========================================================
 * NotificationPopover — 5 Njoftimet e Fundit (Engagement #53)
 * =========================================================
 * Qëllimi: Popover kompakt me 5 njoftimet e fundit + dot i kuq.
 *          Like ❤ / Koment 💬 / Termin 📅 / Abonim 💳
 *          E ndara nga NotificationBell — mund të integrohet
 *          kudo si komponent standalone.
 * Endpoint: GET /api/v1/notifications
 * =========================================================
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Calendar, CheckCheck, CreditCard, Heart, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api/client";

interface NotificationOut {
  id: number; type: string; title: string;
  body: string | null; link: string | null;
  is_read: boolean; created_at: string;
}

const TYPE_ICON: Record<string, React.ElementType> = {
  like:         Heart,
  comment:      MessageCircle,
  appointment:  Calendar,
  subscription: CreditCard,
};
const TYPE_COLOR: Record<string, string> = {
  like:         "text-rose-500",
  comment:      "text-blue-500",
  appointment:  "text-emerald-500",
  subscription: "text-amber-500",
};
const TYPE_LINK: Record<string, string> = {
  appointment: "/app/patient/appointments",
  lab: "/app/patient/history",
  lab_result: "/app/laboratory",
  report: "/app/patient/history",
  payment: "/app/wallet",
  wallet: "/app/wallet",
  message: "/app/messages",
  comment: "/app/messages",
  like: "/app/messages",
  share: "/app/messages",
  referral: "/app/messages",
};

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)    return "tani";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const fetchNotifs = async (): Promise<NotificationOut[]> =>
  (await apiClient.get<NotificationOut[]>("/notifications")).data;

export const useNotifications = () => useQuery({
  queryKey: ["notifications"],
  queryFn: fetchNotifs,
  refetchInterval: 30_000,
  refetchIntervalInBackground: false,
  staleTime: 15_000,
});

function resolveNotificationHref(n: NotificationOut): string | null {
  if (n.link && n.link.trim().length > 0) return n.link;
  return TYPE_LINK[n.type] ?? null;
}

export const NotificationPopover = (): JSX.Element => {
  const [open, setOpen] = useState(false);
  const ref             = useRef<HTMLDivElement>(null);
  const navigate        = useNavigate();
  const qc              = useQueryClient();

  const { data: all = [] } = useNotifications();

  const { mutate: markRead } = useMutation({
    mutationFn: (id: number) => apiClient.post(`/notifications/${id}/read`),
    onSuccess:  () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const { mutate: markAllRead } = useMutation({
    mutationFn: async () => {
      const unread = all.filter((n) => !n.is_read);
      await Promise.all(unread.map((n) => apiClient.post(`/notifications/${n.id}/read`)));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const unread  = all.filter((n) => !n.is_read).length;
  const preview = all.slice(0, 5);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleNotificationClick = (n: NotificationOut) => {
    const href = resolveNotificationHref(n);
    if (!n.is_read) markRead(n.id);
    if (href) navigate(href);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button type="button" aria-label="Njoftimet" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-sm transition hover:bg-secondary"
      >
        <Bell className="h-4.5 w-4.5" aria-hidden="true" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-0.5 text-[9px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
        <button
          type="button"
          aria-label="Mbyll njoftimet"
          className="fixed inset-0 z-[69] bg-background/65 backdrop-blur-sm sm:hidden"
          onClick={() => setOpen(false)}
        />
        <div className="fixed left-3 right-3 top-20 z-[70] max-h-[calc(100vh-6rem)] overflow-y-auto rounded-[22px] border border-border bg-card p-4 text-card-foreground shadow-2xl sm:absolute sm:left-auto sm:right-0 sm:top-11 sm:w-80 sm:max-w-[calc(100vw-1.5rem)] sm:overflow-visible">
          {/* Header */}
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-foreground">Njoftimet</p>
              {unread > 0 && (
                <p className="text-[10px] text-muted-foreground">{unread} të palexuara</p>
              )}
            </div>
            {unread > 0 && (
              <button type="button" onClick={() => markAllRead()}
                className="flex items-center gap-1 text-[10px] text-primary transition hover:opacity-80">
                <CheckCheck className="h-3 w-3" />Shëno gjitha
              </button>
            )}
          </div>

          {/* Lista — 5 të fundit */}
          {preview.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">
              Nuk ka njoftime të reja.
            </p>
          ) : (
            <div className="space-y-0.5">
              {preview.map((n) => {
                const Icon  = TYPE_ICON[n.type]  ?? Bell;
                const color = TYPE_COLOR[n.type] ?? "text-muted-foreground";
                return (
                  <button key={n.id} type="button" onClick={() => handleNotificationClick(n)}
                    className={`flex w-full items-start gap-2.5 rounded-[14px] p-2.5 text-left transition hover:bg-secondary ${n.is_read ? "opacity-70" : ""}`}
                  >
                    <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary/80 ${color}`}>
                      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 text-xs font-semibold text-foreground">{n.title}</p>
                      {n.body && (
                        <p className="mt-0.5 line-clamp-1 text-[10px] text-muted-foreground">{n.body}</p>
                      )}
                      <p className="mt-0.5 text-[9px] text-muted-foreground">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.is_read && (
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div className="mt-3 border-t border-border pt-2.5 text-center">
            <button type="button"
              onClick={() => { navigate("/app/messages"); setOpen(false); }}
              className="text-[10px] font-semibold text-primary transition hover:opacity-80">
              Shiko të gjitha njoftimet →
            </button>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

/**
 * =========================================================
 * Fund i NotificationPopover
 * =========================================================
 */
